import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, CheckSquare, Search, Upload, Paperclip, Image, X, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TaskManagement({ employees }) {
  const [tasks, setTasks] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [wizardStep, setWizardStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [completionDialog, setCompletionDialog] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionAttachments, setCompletionAttachments] = useState([]);
  const [completionUploading, setCompletionUploading] = useState(false);
  const [taskForm, setTaskForm] = useState({
    assigned_to: '',
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    attachments: []
  });

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.sub || payload.user_id || payload.id);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTasks = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    const newAttachments = [];

    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 50MB per file.`);
        continue;
      }

      const formData = new FormData();
      const isVideo = file.type.startsWith('video/');
      
      if (isVideo) {
        formData.append('file', file);
        try {
          const response = await fetch(`${API_URL}/api/upload-video`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          if (response.ok) {
            const data = await response.json();
            newAttachments.push({ url: data.url, name: file.name, type: 'video', size: file.size });
          }
        } catch (err) {
          toast.error(`Failed to upload ${file.name}`);
        }
      } else {
        formData.append('files', file);
        try {
          const response = await fetch(`${API_URL}/api/upload-images`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          if (response.ok) {
            const data = await response.json();
            if (data.urls && data.urls.length > 0) {
              newAttachments.push({ url: data.urls[0], name: file.name, type: file.type.startsWith('image/') ? 'image' : 'document', size: file.size });
            }
          }
        } catch (err) {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    }

    setTaskForm(prev => ({ ...prev, attachments: [...prev.attachments, ...newAttachments] }));
    setUploading(false);
    if (newAttachments.length > 0) toast.success(`${newAttachments.length} file(s) uploaded`);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setTaskForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
  };

  const handleCreateTask = async () => {
    if (!taskForm.assigned_to || !taskForm.title) {
      toast.error('Please select an employee and enter a task title');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (editingTaskId) {
        // Update existing task
        const response = await fetch(`${API_URL}/api/tasks/${editingTaskId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: taskForm.title,
            description: taskForm.description,
            due_date: taskForm.due_date,
            priority: taskForm.priority
          })
        });
        if (response.ok) {
          toast.success('Task updated');
          setShowCreateDialog(false);
          setEditingTaskId(null);
          setWizardStep(1);
          setTaskForm({ assigned_to: '', title: '', description: '', due_date: '', priority: 'medium', attachments: [] });
          fetchTasks();
        } else {
          toast.error('Failed to update task');
        }
      } else {
        // Create new task
        const response = await fetch(`${API_URL}/api/tasks`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(taskForm)
        });
        if (response.ok) {
          toast.success('Task created and employee notified');
          setShowCreateDialog(false);
          setWizardStep(1);
          setTaskForm({ assigned_to: '', title: '', description: '', due_date: '', priority: 'medium', attachments: [] });
          fetchTasks();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create task');
        }
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDeleteTask = async (taskId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Task deleted');
        fetchTasks();
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setTaskForm({
      assigned_to: task.assigned_to,
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority || 'medium',
      attachments: task.attachments || []
    });
    setWizardStep(1);
    setShowCreateDialog(true);
  };

  const handleCompletionUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setCompletionUploading(true);
    const newAtts = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('files', file);
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
        });
        if (res.ok) {
          const data = await res.json();
          if (data.urls?.length > 0) newAtts.push({ url: data.urls[0], name: file.name, type: file.type.startsWith('image') ? 'image' : 'document' });
        }
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setCompletionAttachments(prev => [...prev, ...newAtts]);
    setCompletionUploading(false);
    e.target.value = '';
  };

  const handleCompleteWithProof = async () => {
    if (!completionDialog) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/tasks/${completionDialog.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'completed',
          completion_notes: completionNotes || undefined,
          completion_attachments: completionAttachments.length > 0 ? completionAttachments : undefined
        })
      });
      if (res.ok) {
        toast.success('Task completed');
        setCompletionDialog(null);
        setCompletionNotes('');
        setCompletionAttachments([]);
        fetchTasks();
      }
    } catch { toast.error('Connection error'); }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Task marked as ${newStatus}`);
        fetchTasks();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesSearch = !searchQuery ||
      (task.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.assigned_to_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const statusCounts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
  };

  const priorityColor = (p) => {
    if (p === 'high') return 'bg-red-100 text-red-700 border-red-200';
    if (p === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const statusColor = (s) => {
    if (s === 'completed') return 'success';
    if (s === 'in_progress') return 'warning';
    return 'default';
  };

  const selectedEmployee = employees.find(e => e.id === taskForm.assigned_to);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5" />
              Task Management
            </CardTitle>
            <div className="flex gap-1 flex-wrap">
              {['all', 'pending', 'in_progress', 'completed'].map(status => (
                <Button key={status} variant={filterStatus === status ? 'default' : 'outline'} size="sm"
                  onClick={() => setFilterStatus(status)} data-testid={`filter-${status}`}>
                  {status === 'all' ? 'All' : status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="ml-1 text-xs">({statusCounts[status]})</span>
                </Button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search tasks or employee..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 w-56" data-testid="task-search" />
            </div>
            <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) { setWizardStep(1); setEditingTaskId(null); } }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingTaskId(null); setTaskForm({ assigned_to: '', title: '', description: '', due_date: '', priority: 'medium', attachments: [] }); }} data-testid="create-task-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Assign Task
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingTaskId ? 'Edit Task' : 'Assign Task'}</DialogTitle>
                </DialogHeader>

                {/* Wizard Progress */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                  {[{ step: 1, label: 'Details' }, { step: 2, label: 'Attachments' }, { step: 3, label: 'Review' }].map(({ step, label }) => (
                    <button key={step} onClick={() => setWizardStep(step)} style={{
                      flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                      background: wizardStep === step ? '#5b63d3' : wizardStep > step ? '#16a34a' : '#e2e8f0',
                      color: wizardStep >= step ? 'white' : '#64748b', fontSize: '0.75rem', fontWeight: 600
                    }} data-testid={`task-wizard-step-${step}`}>
                      {step}. {label}
                    </button>
                  ))}
                </div>

                {/* Step 1: Task Details */}
                {wizardStep === 1 && (
                  <div className="space-y-4">
                    <div>
                      <Label>Assign To *</Label>
                      <Select value={taskForm.assigned_to} onValueChange={(val) => setTaskForm({ ...taskForm, assigned_to: val })}>
                        <SelectTrigger data-testid="task-assign-to"><SelectValue placeholder="Select employee" /></SelectTrigger>
                        <SelectContent>
                          {[...employees].sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || '')).map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.user?.name || emp.name} {emp.position ? `- ${emp.position}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Task Title *</Label>
                      <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                        placeholder="e.g. Complete insulation on 2nd floor" required data-testid="task-title" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                        placeholder="Detailed instructions..." rows={3} data-testid="task-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Due Date</Label>
                        <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} data-testid="task-due-date" />
                      </div>
                      <div>
                        <Label>Priority</Label>
                        <Select value={taskForm.priority} onValueChange={(val) => setTaskForm({ ...taskForm, priority: val })}>
                          <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button onClick={() => setWizardStep(2)} disabled={!taskForm.assigned_to || !taskForm.title} data-testid="task-wizard-next-1">
                        Next: Attachments →
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Attachments */}
                {wizardStep === 2 && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">Attach photos, videos, or documents to help the employee understand the task. (Optional)</p>
                    
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input type="file" id="task-file-upload" multiple accept="image/*,video/*,.pdf,.doc,.docx"
                        onChange={handleFileUpload} style={{ display: 'none' }} />
                      <label htmlFor="task-file-upload" style={{ cursor: 'pointer' }}>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Tap to upload files</p>
                        <p className="text-xs text-gray-500 mt-1">Photos, Videos, PDFs (max 50MB each)</p>
                      </label>
                    </div>
                    {uploading && <p className="text-sm text-blue-600 text-center">Uploading...</p>}

                    {taskForm.attachments.length > 0 && (
                      <div className="space-y-2">
                        <Label>Attached Files ({taskForm.attachments.length})</Label>
                        {taskForm.attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border">
                            {att.type === 'image' ? (
                              <img src={`${API_URL}${att.url}`} alt={att.name} className="w-12 h-12 object-cover rounded" />
                            ) : att.type === 'video' ? (
                              <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-blue-600 text-xs font-bold">VID</div>
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center"><Paperclip className="w-5 h-5 text-gray-500" /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{att.name}</p>
                              <p className="text-xs text-gray-500">{(att.size / 1024).toFixed(0)} KB</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeAttachment(idx)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between pt-2">
                      <Button variant="outline" onClick={() => setWizardStep(1)}>← Back</Button>
                      <Button onClick={() => setWizardStep(3)} data-testid="task-wizard-next-2">
                        Next: Review →
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Review */}
                {wizardStep === 3 && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4 border space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Assign To:</span>
                        <span className="text-sm font-semibold">{selectedEmployee?.user?.name || selectedEmployee?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Title:</span>
                        <span className="text-sm font-semibold">{taskForm.title}</span>
                      </div>
                      {taskForm.description && (
                        <div>
                          <span className="text-sm text-gray-500">Description:</span>
                          <p className="text-sm mt-1">{taskForm.description}</p>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Priority:</span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${priorityColor(taskForm.priority)}`}>{taskForm.priority}</span>
                      </div>
                      {taskForm.due_date && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Due Date:</span>
                          <span className="text-sm">{new Date(taskForm.due_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Attachments:</span>
                        <span className="text-sm">{taskForm.attachments.length} file(s)</span>
                      </div>
                      <Button variant="link" size="sm" onClick={() => setWizardStep(1)} className="p-0 h-auto text-xs">Edit Details</Button>
                    </div>

                    {taskForm.attachments.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {taskForm.attachments.map((att, idx) => (
                          att.type === 'image' ? (
                            <img key={idx} src={`${API_URL}${att.url}`} alt={att.name} className="w-16 h-16 object-cover rounded border" />
                          ) : (
                            <div key={idx} className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center text-xs text-center p-1">
                              {att.type === 'video' ? 'Video' : att.name.split('.').pop().toUpperCase()}
                            </div>
                          )
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between pt-2">
                      <Button variant="outline" onClick={() => setWizardStep(2)}>← Back</Button>
                      <Button onClick={handleCreateTask} data-testid="submit-task-btn">
                        Assign Task
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredTasks.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {tasks.length === 0 ? 'No tasks created yet. Click "Assign Task" to get started.' : 'No tasks match your filter.'}
          </p>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map(task => (
              <div key={task.id} className="flex items-start justify-between p-4 rounded-lg border bg-white hover:shadow-sm transition-shadow"
                data-testid={`task-${task.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold">{task.title}</span>
                    <Badge variant={statusColor(task.status)} className="text-xs">
                      {task.status === 'in_progress' ? 'In Progress' : task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
                    </Badge>
                    <span className={`text-xs px-2 py-0.5 rounded border ${priorityColor(task.priority)}`}>{task.priority}</span>
                    {(task.attachments?.length > 0 || task.completion_attachments?.length > 0) && (
                      <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </div>
                  {task.description && <p className="text-sm text-gray-600 mb-1">{task.description}</p>}
                  
                  {/* Show attachments thumbnails */}
                  {task.attachments?.length > 0 && (
                    <div className="flex gap-1 mb-1 flex-wrap">
                      {task.attachments.map((att, idx) => (
                        att.type === 'image' ? (
                          <a key={idx} href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${API_URL}${att.url}`} alt={att.name} className="w-10 h-10 object-cover rounded border hover:opacity-80" />
                          </a>
                        ) : (
                          <a key={idx} href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                            className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-xs hover:bg-gray-200">
                            {att.type === 'video' ? 'VID' : 'DOC'}
                          </a>
                        )
                      ))}
                    </div>
                  )}

                  {/* Completion attachments */}
                  {task.completion_attachments?.length > 0 && (
                    <div className="mb-1">
                      <p className="text-xs text-green-600 font-medium mb-1">Completion proof:</p>
                      <div className="flex gap-1 flex-wrap">
                        {task.completion_attachments.map((att, idx) => (
                          <a key={idx} href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${API_URL}${att.url}`} alt="proof" className="w-10 h-10 object-cover rounded border border-green-300 hover:opacity-80" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Assigned to: <strong>{task.assigned_to_name || 'Unknown'}</strong></span>
                    {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                    {task.created_by_name && <span>By: {task.created_by_name}</span>}
                    <span>{new Date(task.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {task.created_by === currentUserId && (
                    <Button size="sm" variant="outline" onClick={() => startEditTask(task)} data-testid={`edit-task-${task.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                  {task.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(task.id, 'in_progress')}>Start</Button>
                  )}
                  {task.status === 'in_progress' && (
                    <Button size="sm" variant="outline" onClick={() => { setCompletionDialog(task); setCompletionNotes(''); setCompletionAttachments([]); }}>Complete</Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTask(task.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Completion Dialog */}
      <Dialog open={!!completionDialog} onOpenChange={(open) => { if (!open) setCompletionDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
          </DialogHeader>
          {completionDialog && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="font-semibold text-sm">{completionDialog.title}</h4>
                <p className="text-xs text-gray-500">Assigned to: {completionDialog.assigned_to_name}</p>
              </div>
              <div>
                <Label>Completion Notes</Label>
                <Textarea value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} placeholder="Describe what was done..." rows={3} />
              </div>
              <div>
                <Label>Upload Proof (photos/documents)</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center mt-1">
                  <input type="file" multiple accept="image/*,video/*,.pdf" onChange={handleCompletionUpload} className="hidden" id="admin-completion-upload" />
                  <label htmlFor="admin-completion-upload" className="cursor-pointer text-sm text-gray-500">Tap to upload files</label>
                </div>
                {completionUploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                {completionAttachments.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {completionAttachments.map((att, idx) => (
                      <div key={idx} className="relative">
                        {att.type === 'image' ? (
                          <img src={`${API_URL}${att.url}`} alt="" className="w-14 h-14 object-cover rounded border" />
                        ) : (
                          <div className="w-14 h-14 bg-gray-100 rounded border flex items-center justify-center text-xs">{att.name?.slice(0, 8)}</div>
                        )}
                        <button className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          onClick={() => setCompletionAttachments(prev => prev.filter((_, i) => i !== idx))}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCompletionDialog(null)}>Cancel</Button>
                <Button onClick={handleCompleteWithProof}>Complete Task</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default TaskManagement;
